// Shared head() helpers for canonical URL + Open Graph metadata.
// Use `buildSeo` inside a route's head() to get a complete meta/links bundle.

const SITE_NAME = "USA Lottery Club";
const SITE_URL = "https://usalotteryclub.com";
const DEFAULT_OG_IMAGE = "/og-image.jpg";
const TWITTER_HANDLE = "@usalotteryclub";

function toAbsolute(p: string): string {
  if (!p) return SITE_URL;
  if (/^https?:\/\//i.test(p)) return p;
  return `${SITE_URL}${p.startsWith("/") ? p : `/${p}`}`;
}

export interface SeoInput {
  title: string;
  description: string;
  path: string;          // canonical pathname, e.g. "/results"
  type?: "website" | "article";
  image?: string;        // absolute or root-relative; optional
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export function buildSeo(input: SeoInput) {
  const { title, description, path, type = "website", image, noindex, jsonLd } = input;
  const canonical = toAbsolute(path);
  const ogImage = toAbsolute(image ?? DEFAULT_OG_IMAGE);
  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:locale", content: "en_US" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: canonical },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: TWITTER_HANDLE },
    { name: "twitter:creator", content: TWITTER_HANDLE },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { property: "og:image", content: ogImage },
    { property: "og:image:alt", content: `${SITE_NAME} — ${title}` },
    { name: "twitter:image", content: ogImage },
    { name: "twitter:image:alt", content: `${SITE_NAME} — ${title}` },
  ];
  if (noindex) meta.push({ name: "robots", content: "noindex,nofollow" });

  const links = noindex
    ? []
    : [
        { rel: "canonical", href: canonical },
        // Single-locale site (US English) — declare self + x-default so
        // crawlers don't treat capitalized/locale-variant URLs as duplicates.
        { rel: "alternate", hrefLang: "en-US", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: canonical },
      ];
  const schemas = (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []).map(
    (s) => absolutizeSchema(s) as Record<string, unknown>,
  );
  const scripts = schemas.map((schema) => ({
    type: "application/ld+json",
    children: JSON.stringify(schema),
  }));
  return { meta, links, scripts };
}

/**
 * schema.org consumers expect absolute URLs. Route head() blocks author
 * root-relative paths, so rewrite the URL-bearing keys before serialising.
 */
const URL_KEYS = new Set(["url", "item", "@id", "image", "mainEntityOfPage", "sameAs", "contentUrl"]);

function absolutizeSchema(value: unknown, key?: string): unknown {
  if (typeof value === "string") {
    return key && URL_KEYS.has(key) && value.startsWith("/") ? toAbsolute(value) : value;
  }
  if (Array.isArray(value)) return value.map((v) => absolutizeSchema(v, key));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, absolutizeSchema(v, k)]),
    );
  }
  return value;
}

/** Convert a kebab-case slug into Title Case ("mega-millions" → "Mega Millions"). */
export function humanizeSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}